import { isHidden } from "@/lib/hidden-features";
import {
  Modal,
  Box,
  Text,
  Group,
  Stack,
  Button,
  Image,
  useMantineColorScheme,
  useMantineTheme,
  ActionIcon,
  Card,
  Slider,
  Tabs,
  ThemeIcon,
} from "@mantine/core";
import { 
  XIcon, 
  ClockIcon, 
  FootprintsIcon, 
  SpeakerHighIcon,
  PlayIcon,
  PauseIcon,
  ArrowCounterClockwiseIcon,
  ArrowClockwiseIcon,
  BookmarkSimpleIcon,
} from "@phosphor-icons/react";
import { useState, useRef, useEffect } from "react";
import { useBookmarkedSessions } from "@/api";

interface PathDetailModalProps {
  opened: boolean;
  onClose: () => void;
  pathData: {
    id?: number;
    title: string;
    description?: string;
    text?: string;
    time: string;
    waypoints: string;
    image?: string;
  } | null;
}

const pathSteps = [
  {
    number: 1,
    title: "Build a Balanced Plate",
    description: "Divide your plate into sections—half filled with colorful vegetables and fruits, one-quarter with whole grains, and one-quarter with lean protein. Add small amounts of healthy fats like olive oil or avocado to complete the meal. This ensures your body gets the right mix of nutrients.",
    completed: true,
  },
  {
    number: 2,
    title: "Choose Whole, Unprocessed Foods",
    description: "Focus on natural, minimally processed foods such as fresh produce, whole grains, nuts, and lean meats. Avoid items high in added sugars, sodium, or artificial ingredients. Reading labels can help you make informed choices.",
    completed: false,
  },
  {
    number: 3,
    title: "Practice Portion Control & Consistency",
    description: "Control portion sizes to prevent overeating and support healthy digestion. Use smaller plates, follow serving size guidelines, and plan your meals ahead. Consistency is key—make balanced eating a daily habit for lasting results.",
    completed: false,
  },
];

export function PathDetailModal({ opened, onClose, pathData }: PathDetailModalProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [activeTab, setActiveTab] = useState<"active" | "bookmark">("active");
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(90); // Default 1:30
  const audioRef = useRef<HTMLAudioElement>(null);

  // Fetch bookmarked sessions for this path
  const { data: bookmarkList = [] } = useBookmarkedSessions(
    pathData?.id ?? 0,
    {
      query: {
        enabled: !!pathData?.id && opened,
        queryKey: ["bookmark", pathData?.id],
      },
    }
  );

  // Audio functionality
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener("loadedmetadata", () => {
        setDuration(audioRef.current?.duration || 90);
      });

      audioRef.current.addEventListener("timeupdate", () => {
        setCurrentTime(audioRef.current?.currentTime || 0);
      });

      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
      });
    }
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!pathData) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="90%"
      radius="md"
      padding={0}
      withCloseButton={false}
      overlayProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        },
      }}
      styles={{
        body: {
          padding: "0 !important",
          margin: "0 !important",
        },
        content: {
          borderRadius: theme.radius.md,
          maxWidth: "1400px",
          backgroundColor: colorScheme === "dark" ? "#000000" : "white",
          padding: "0 !important",
          margin: "0 !important",
          overflow: "hidden",
        },
        inner: {
          padding: "0 !important",
          margin: "0 !important",
        },
        header: {
          padding: "0 !important",
          margin: "0 !important",
        },
      }}
    >
      <Box
        style={{
          padding: "16px 24px",
          borderBottom: `1px solid ${colorScheme === "dark" ? theme.colors.neutral[7] : theme.colors.neutral[2]}`,
          background: colorScheme === "dark" ? "#000000" : "white",
          margin: "0 !important",
          paddingTop: "16px",
          paddingBottom: "16px",
          paddingLeft: "24px",
          paddingRight: "24px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Group justify="space-between" align="center">
          <Text fw={600} size="xl" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
            {pathData.title}
          </Text>
          <ActionIcon
            variant="subtle"
            onClick={onClose}
            size="lg"
            radius="md"
            style={{
              backgroundColor: colorScheme === "dark" ? "transparent" : "transparent",
              color: colorScheme === "dark" ? "white" : theme.colors.neutral[9],
            }}
          >
            <XIcon size={20} />
          </ActionIcon>
        </Group>
      </Box>

      <Box 
        p="xl"
        style={{
          background: colorScheme === "dark" 
            ? "#000000" 
            : "linear-gradient(90deg, #FFF8F6 0%, #FAF8FB 50%, #F5F8FA 100%)",
        }}
      >

        {/* Two Column Layout */}
        <Group align="stretch" gap="xl" wrap="nowrap">
          {/* Left Column - Summary */}
          <Box style={{ flex: "0 0 35%", maxWidth: "450px", minWidth: "300px", display: "flex" }}>

            <Card
              withBorder
              radius="md"
              p="lg"
              style={{
                backgroundColor: colorScheme === "dark" ? theme.colors.neutral[8] : theme.colors.neutral[0],
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack gap="md">
                {/* Image Placeholder */}
                <Box
                  style={{
                    width: "100%",
                    height: 280,
                    backgroundColor: colorScheme === "dark" ? theme.colors.neutral[7] : theme.colors.neutral[2],
                    borderRadius: theme.radius.md,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {pathData.image ? (
                    <Image
                      src={pathData.image}
                      alt={pathData.title}
                      height={280}
                      w="100%"
                      fit="cover"
                      radius="md"
                    />
                  ) : (
                    <Text size="sm" c="dimmed">
                      Image Placeholder
                    </Text>
                  )}
                </Box>

                {/* Description */}
                <Text 
                  size="sm" 
                  c={colorScheme === "dark" ? theme.colors.neutral[2] : theme.colors.neutral[7]}
                  style={{ lineHeight: 1.6 }}
                >
                  {pathData.description || pathData.text || "Learn how to build balanced meals with nutrient-rich foods that fuel your body and support long-term wellness."}
                </Text>

                {/* Metadata */}
                <Group gap="md" mt="xs">
                  <Group gap={4}>
                    <ClockIcon size={18} color={colorScheme === "dark" ? "#247ED3" : theme.colors.indigo[9]} />
                    <Text size="sm">{pathData.time}</Text>
                  </Group>
                  <Group gap={4}>
                    <FootprintsIcon size={18} color={theme.colors.green[9]} />
                    <Text size="sm">{pathData.waypoints}</Text>
                  </Group>
                </Group>
              </Stack>
            </Card>
          </Box>

          {/* Right Column - Path Progress */}
          <Box style={{ flex: 1, minWidth: 0, maxWidth: "100%", display: "flex" }}>
            <Card
              withBorder
              radius="md"
              p="lg"
              style={{
                backgroundColor: colorScheme === "dark" ? theme.colors.neutral[8] : theme.colors.neutral[0],
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack gap="md" style={{ flex: 1 }}>
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <Tabs
                    value={activeTab}
                    onChange={(value) => setActiveTab(value as "active" | "bookmark")}
                    styles={{ tab: { fontSize: 16, padding: "12px 20px" } }}
                    style={{ flex: 1 }}
                  >
                    <Tabs.List grow>
                      <Tabs.Tab
                        value="active"
                        style={{
                          background:
                            activeTab === "active"
                              ? "linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)"
                              : undefined,
                          color: activeTab === "active" ? "white" : undefined,
                          borderTopLeftRadius: "9px",
                          borderTopRightRadius: "9px",
                        }}
                        onMouseEnter={(e) => {
                          if (activeTab !== "active") {
                            e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                              ? "#555555"
                              : "#E0EDFE";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeTab !== "active") {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }
                        }}
                      >
                        Active Path
                      </Tabs.Tab>
                      <Tabs.Tab
                        value="bookmark"
                        style={{
                          background:
                            activeTab === "bookmark"
                              ? "linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)"
                              : undefined,
                          color: activeTab === "bookmark" ? "white" : undefined,
                          borderTopLeftRadius: "9px",
                          borderTopRightRadius: "9px",
                        }}
                        onMouseEnter={(e) => {
                          if (activeTab !== "bookmark") {
                            e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                              ? "#555555"
                              : "#E0EDFE";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeTab !== "bookmark") {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }
                        }}
                      >
                        Bookmark
                      </Tabs.Tab>
                    </Tabs.List>

                    {/* Active Path Panel */}
                    <Tabs.Panel value="active" pt="md">
                      <Stack gap="md">
                        {/* Path Progress Title with Listen Button */}
                        <Group justify="space-between" align="center">
                          <Text 
                            fw={600} 
                            size="lg"
                            c={colorScheme === "dark" ? "white" : theme.colors.neutral[9]}
                          >
                            Path Progress
                          </Text>
                          {!isHidden("listen") && (
                            <Button
                              variant="filled"
                              leftSection={<SpeakerHighIcon size={16} />}
                              size="sm"
                              style={{
                                backgroundColor: colorScheme === "dark" ? "#1662B3" : "#124B8C",
                                color: "white",
                              }}
                              onClick={() => setShowAudioPlayer(!showAudioPlayer)}
                            >
                              Listen
                            </Button>
                          )}
                        </Group>

                        {/* Audio Player Section */}
                        {showAudioPlayer && (
                          <Card withBorder radius="md" p="md" mt="md">
                            <Group justify="space-between" mb="md">
                              <Text fw={600} size="lg">
                                Listen your Path
                              </Text>
                              <ActionIcon
                                variant="subtle"
                                onClick={() => setShowAudioPlayer(false)}
                              >
                                <XIcon size={20} />
                              </ActionIcon>
                            </Group>

                            <Box
                              p="xs"
                              style={{
                                background: `linear-gradient(90deg, ${theme.colors.indigo[8]} 0%, ${theme.colors.negative[4]} 100%)`,
                                borderRadius: theme.radius.md,
                                color: "white",
                              }}
                            >
                              <Group align="center" gap="xs">
                                <ActionIcon
                                  size="lg"
                                  variant="subtle"
                                  color="white"
                                  onClick={skipBackward}
                                >
                                  <ArrowCounterClockwiseIcon size={24} />
                                </ActionIcon>

                                <ActionIcon
                                  radius="xl"
                                  size="xl"
                                  variant="filled"
                                  color="white"
                                  style={{
                                    backgroundColor: "white",
                                    color: theme.colors.indigo[8],
                                  }}
                                  onClick={togglePlay}
                                >
                                  {isPlaying ? (
                                    <PauseIcon size={24} weight="fill" />
                                  ) : (
                                    <PlayIcon size={24} weight="fill" />
                                  )}
                                </ActionIcon>

                                <ActionIcon
                                  size="lg"
                                  variant="subtle"
                                  color="white"
                                  onClick={skipForward}
                                >
                                  <ArrowClockwiseIcon size={24} />
                                </ActionIcon>
                                <Text
                                  size="sm"
                                  c={theme.colors.neutral[2]}
                                  style={{ minWidth: 45 }}
                                >
                                  {formatTime(duration)}
                                </Text>

                                <Slider
                                  value={currentTime}
                                  onChange={(value) => {
                                    if (audioRef.current) {
                                      audioRef.current.currentTime = value;
                                    }
                                  }}
                                  max={duration}
                                  min={0}
                                  step={0.1}
                                  style={{ flex: 1 }}
                                  size="sm"
                                  color="white"
                                  styles={{
                                    track: {
                                      backgroundColor: theme.colors.blue[5],
                                      border: "none",
                                    },
                                    bar: {
                                      background: theme.colors.blue[5],
                                    },
                                    thumb: {
                                      boxSizing: "content-box",
                                    },
                                  }}
                                />
                              </Group>
                            </Box>
                          </Card>
                        )}

                        <Stack gap="lg">
                          {pathSteps.map((step, index) => (
                            <Box key={step.number}>
                              <Group align="flex-start" gap="md" wrap="nowrap">
                                <Box style={{ position: "relative", flexShrink: 0 }}>
                                  <Box
                                    style={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: "50%",
                                      backgroundColor: colorScheme === "dark" ? "white" : (step.completed ? theme.colors.blue[6] : theme.colors.neutral[3]),
                                      color: colorScheme === "dark" ? theme.colors.neutral[9] : (step.completed ? "white" : theme.colors.neutral[6]),
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: 600,
                                      fontSize: "14px",
                                    }}
                                  >
                                    {step.number}
                                  </Box>
                                  {index < pathSteps.length - 1 && (
                                    <Box
                                      style={{
                                        position: "absolute",
                                        left: "50%",
                                        top: "32px",
                                        transform: "translateX(-50%)",
                                        width: "2px",
                                        height: "40px",
                                        backgroundColor: colorScheme === "dark" ? theme.colors.neutral[7] : theme.colors.neutral[3],
                                      }}
                                    />
                                  )}
                                </Box>

                                {/* Step Content */}
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                  <Text 
                                    fw={600} 
                                    size="md" 
                                    mb="xs"
                                    c={colorScheme === "dark" ? "white" : theme.colors.neutral[9]}
                                  >
                                    {step.title}
                                  </Text>
                                  <Text 
                                    size="sm" 
                                    c={colorScheme === "dark" ? "white" : theme.colors.neutral[7]}
                                  >
                                    {step.description}
                                  </Text>
                                </Box>
                              </Group>
                            </Box>
                          ))}
                        </Stack>
                      </Stack>
                    </Tabs.Panel>

                    {/* Bookmark Panel */}
                    <Tabs.Panel value="bookmark" pt="md">
                      <Stack gap="md">
                        <Text 
                          fw={500} 
                          size="md"
                          c={colorScheme === "dark" ? "white" : theme.colors.neutral[9]}
                        >
                          Bookmark
                        </Text>

                        {Array.isArray(bookmarkList) && bookmarkList.length > 0 ? (
                          <Stack gap="sm">
                            {bookmarkList.map((data: any) => (
                              <Group
                                key={data?.id}
                                p="sm"
                                style={{
                                  backgroundColor: colorScheme === "dark"
                                    ? theme.colors.neutral[8]
                                    : theme.colors.neutral[1],
                                  borderRadius: theme.radius.md,
                                  cursor: "pointer",
                                }}
                                justify="flex-start"
                              >
                                <ThemeIcon
                                  color={theme.colors.informative[7]}
                                  size={30}
                                  radius="xl"
                                >
                                  <BookmarkSimpleIcon
                                    size={20}
                                    color="white"
                                  />
                                </ThemeIcon>
                                <Text 
                                  size="sm"
                                  c={colorScheme === "dark" ? "white" : theme.colors.neutral[9]}
                                >
                                  {data?.title ?? ""}
                                </Text>
                              </Group>
                            ))}
                          </Stack>
                        ) : (
                          <Text 
                            size="sm" 
                            c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[6]}
                            style={{ fontStyle: "italic" }}
                          >
                            No bookmarked sessions found
                          </Text>
                        )}
                      </Stack>
                    </Tabs.Panel>
                  </Tabs>
                </Group>
              </Stack>
            </Card>
          </Box>
        </Group>
      </Box>
    </Modal>
  );
}

